<template>
  <div class="store-maplist">
    <div class="google-map-result" :class="{ 'rfp-stlc-hide-map': !displayMap }">
      <div id="map">Map</div>
    </div>
    <rfp-storelocator-map-popup
      :render-popup="renderPopup"
      :location="location"
      :distance-enabled="distanceEnabled"
      :hide-get-direction-link="hideGetDirectionLink == true"
      :distance-type="distanceTypes"
      :get-directions="getDirections"
      :get-direction-aria-label="getDirectionAriaLabel"
    />
  </div>
</template>

<script>
import eventBus from '@refapp/eventbus-js';

/* global google */

/**
 * @module Component/GoogleMapx
 * @desc the GoogleMap tag implement
 */
import GoogleAPI from '../mixins/rfp-storelocator-google-api';
import datalayerMixin from '../mixins/datalayer-mixin';
import { tryParseJson } from '../mixins/utils';
import rfpStorelocatorMapPopup from '../map-popup/rfp-storelocator-map-popup.vue';
import { DisplayMode } from '../mixins/constants';

function colorPropValidator(value) {
  return /^#([0-9a-f]{3}){1,2}$/i.test(value); // HEX format
}

export default {
  /**
   * @member {type} name tag name of the component
   * @readonly
   */
  name: 'rfp-storelocator-google-map',
  components: {
    'rfp-storelocator-map-popup': rfpStorelocatorMapPopup,
  },
  mixins: [GoogleAPI, datalayerMixin],
  props: {
    latitude: {
      type: String,
      required: true,
    },
    longitude: {
      type: String,
      required: true,
    },
    zoomLevel: {
      type: Number,
      required: true,
    },
    mapUrl: {
      type: String,
      required: true,
    },
    defaultCountry: {
      type: String,
      required: false,
      default: '',
    },
    iriEnabled: {
      type: Boolean,
      required: false,
      default: true,
    },
    clickableIcons: {
      type: Boolean,
      required: false,
      default: false,
    },
    upcCode: {
      type: String,
      required: false,
      default: '',
    },
    storesPerPage: {
      type: Number,
      required: true,
    },
    storesPerPageWithZoom: {
      type: Number,
      default: 50,
    },
    defaultRadius: {
      type: String,
      required: true,
    },
    defaultDistanceMetric: {
      type: String,
      required: true,
    },
    filterByDistanceEnable: {
      type: Boolean,
      required: true,
    },
    getDirections: {
      type: String,
      required: false,
      default: '',
    },
    showHideGooglemapText: {
      type: String,
      required: false,
      default: '',
    },
    hideGetDirectionLink: {
      type: Boolean,
      default: false,
    },
    getDirectionTags: {
      type: String,
      required: false,
      default: '',
    },
    callStoreTags: {
      type: String,
      required: false,
      default: '',
    },
    bookStoreTags: {
      type: String,
      required: false,
      default: '',
    },
    autocompleteType: {
      type: String,
      required: true,
      default: '',
    },
    getDirectionAriaLabel: {
      type: String,
      default: 'Get directions to [location]',
    },
    displayMode: {
      type: String,
      required: false,
      default: DisplayMode.List,
      validator(value) {
        return Object.values(DisplayMode).includes(value);
      },
    },
    languageCode: {
      type: String,
      required: true,
    },
    markerIconUrl: {
      type: String,
      default: '',
    },
    markerLabelColor: {
      type: String,
      default: '#FFFFFF',
      validator: colorPropValidator,
    },
    markerSize: {
      type: Number,
      default: 25,
    },
    disableListItemNumbering: {
      type: Boolean,
      default: false,
    },
    mapCtaText: {
      type: String,
      default: 'Search in this area',
    },
    currentAreaSearchTaggingData: {
      type: Object,
      default: () => ({}),
    },
  },
  /**
   * @function data
   * @desc init the data when the component mount
   */
  data() {
    return {
      resultCount: -1, // list hasn't loaded, positive value or 0 after
      storeDetails: [],
      storeStatus: false,
      item: [],
      test: '',
      showToggle: false,
      displayMap: true,
      distanceEnabled: false,
      categoryEnabled: '',
      typemodel: '',
      productGroupId: '',
      productGroupEnabled: '',
      distances: '',
      distanceTypes: '',
      zip: '',
      upc: '',
      pageNo: 1,
      filterByAttribute: [],
      location: {},
      renderPopup: false,
      showSearchButton: false,
      searchButton: null,
      searchButtonClass: 'search-in-area-button',
      autocomplete: null, // Store autocomplete instance to prevent duplicate listeners
    };
  },
  watch: {
    showSearchButton() {
      this.handleSearchButtonRenderLogic();
    },
  },
  created() {
    eventBus.on('toggleMap', (value) => {
      this.displayMap = value;
    });

    // Create a media condition that targets viewports at least 768px wide
    this._mapMediaQuery = window.matchMedia('(max-width: 768px)');
    this._mapMediaQueryHandler = () => this.syncMapViewport();
    this.syncMapViewport();
    this._mapMediaQuery.addEventListener('change', this._mapMediaQueryHandler);

    // Assign the default lat and lon value
    this.lat = this.latitude.replace(/,/g, '.');
    this.lon = this.longitude.replace(/,/g, '.');
    this.distanceTypes = this.defaultDistanceMetric;
    this.distances = this.defaultRadius;
    this.typemodel = 'ALL';
    this.mapConfig.zoom = Number(this.zoomLevel);
    this.mapConfig.clickableIcons = this.clickableIcons === true;
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = this.mapUrl;
    document.body.appendChild(script);
    script.onload = async () => await this.init(true);

    const latLng = {
      lat: this.lat,
      lon: this.lon,
      typemodel: this.typemodel,
      productGroupId: this.productGroupId,
      distances: this.distances,
      distanceTypes: this.distanceTypes,
      pageNo: 1,
    };
    eventBus.emit('promotionMapData', latLng);

    eventBus.on('productGroupId-updated', (value) => {
      ((this.productGroupId = value.productGroupId),
        (this.productGroupEnabled = value.productGroupEnabled));
    });
    eventBus.on('storeMsg', () => {
      this.listStatus = 'none';
      this.storeStatus = true;
    });
    eventBus.on('mapData', async (value) => {
      this.lat = value.lat;
      this.lon = value.lon;
      this.typemodel = value.typemodel || 'ALL';
      this.distances = value.distances;
      this.distanceTypes = value.distanceTypes;

      if (typeof value.distanceEnabled === 'boolean') {
        this.distanceEnabled = value.distanceEnabled;
      }

      this.categoryEnabled = value.categoryEnabled;
      this.productGroupEnabled = value.productGroupEnabled;
      this.zip = value.zip;
      this.upc = value.upc;
      this.pageNo = value.pageNo;
      this.productGroupId = value.productGroupId;
      this.filterByAttribute = value.filterByAttribute;
      await this.init(false);
    });
    eventBus.on('presetFilters', (value) => {
      if (value.distanceTypes) {
        this.distanceTypes = value.distanceTypes;
      }

      if (typeof value.distanceEnabled === 'boolean') {
        this.distanceEnabled = value.distanceEnabled;
      }

      if (value.filterByAttribute) {
        this.filterByAttribute = value.filterByAttribute;
      }

      if (value.productGroupId) {
        this.productGroupId = value.productGroupId;
      }

      if (value.productGroupEnabled) {
        this.productGroupEnabled = value.productGroupEnabled;
      }

      if (value.distances) {
        this.distances = value.distances;
      }

      if (value.typemodel) {
        this.typemodel = value.typemodel;
      }
    });
    eventBus.emit('rfp-after-loaded-page');

    eventBus.on('pagination_record_count', (datas) => {
      this.resultCount = datas.TotalStoreCount;
    });

    // listen the storeData event from other mixins
    eventBus.on('storeData', ({ value, page }) => {
      this.storeDetails = value;
      eventBus.emit('pagination', { value, page });
      if (
        document.getElementsByClassName('salonstore-list') &&
        document.getElementsByClassName('salonstore-list').length > 0
      ) {
        if (this.resultCount > 0) {
          // It's rendering based on search data
          eventBus.emit('updateMarker', value);
          if (this.storeStatus) {
            this.listStatus = 'block';
          }
        } else {
          // It's rendering default based on data
          this.listStatus = 'block';
          eventBus.emit('salonList');
        }
      } else if (this.storeDetails.length > 0) {
        // It's rendering only for storelocatorpdp
        eventBus.emit('updateMarker', value);
        this.listStatus = 'block';
      } else {
        this.resetMarkers();
      }
    });

    eventBus.on('rfpTaggingBookSaloon', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.bookStoreTags),
        values: {
          storeName: storeName,
        },
      });
    });
    eventBus.on('rfpTaggingCallStore', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.callStoreTags),
        values: {
          storeName: storeName,
        },
      });
    });
    eventBus.on('rfpTaggingGetDirections', (storeName) => {
      eventBus.emit('rfpTaggingPushToDatalayer', {
        tags: tryParseJson(this.getDirectionTags),
        values: {
          storeName: storeName,
        },
      });
    });
  },
  beforeDestroy() {
    this.handleBeforeUnmount();
  },
  beforeUnmount() {
    this.handleBeforeUnmount();
  },
  methods: {
    async init(status) {
      // NOTE: this.initMap is exposed by the GoogleAPI mixin
      await this.initMap();

      // Skip if IRI is enabled (only supports ZIP code search) or autocomplete already exists
      const input = document.getElementById('txtZIPCODE');
      const shouldUseAutocomplete = !this.iriEnabled && google.maps.places;
      const defaultCountryCode = this.defaultCountry;
      if (shouldUseAutocomplete && !this.autocomplete) {
        this.autocomplete = new google.maps.places.Autocomplete(input);

        if (defaultCountryCode) {
          this.autocomplete.setComponentRestrictions({ country: defaultCountryCode.split(',') });
          this.autocomplete.setFields(['address_components', 'geometry']);
          this.autocomplete.setTypes([this.autocompleteType]);
        }

        // Listen for place selection and trigger automatic search
        this.autocomplete.addListener('place_changed', () => {
          const place = this.autocomplete.getPlace();
          eventBus.emit('autocomplete-place-selected', place);
        });
      }

      // Re-bind bounds to the current map so it stays in sync when a new map is created
      if (shouldUseAutocomplete && !defaultCountryCode) {
        this.autocomplete.bindTo('bounds', this.map);
      }

      if (status) {
        eventBus.emit('currentPlace');
      }
      this.bindGMapListeners(true);
    },
    syncMapViewport() {
      this.showToggle = this._mapMediaQuery.matches;
      this.displayMap = this._mapMediaQuery.matches ? this.displayMode !== DisplayMode.List : true;
    },
    handleSearchButtonRenderLogic() {
      if (this.showSearchButton) {
        if (!this.searchButton) {
          this.searchButton = document.createElement('button');
          this.searchButton.textContent = this.mapCtaText;
          this.searchButton.classList.add(this.searchButtonClass);
          this.searchButton.addEventListener('click', this.handleSearchInAreaClick);
          this.map.controls[google.maps.ControlPosition.TOP_CENTER].push(this.searchButton);
        }
      } else {
        this.resetSearchButtonState();
      }
    },
    resetSearchButtonState() {
      if (this.searchButton) {
        this.searchButton.removeEventListener('click', this.handleSearchInAreaClick);
        this.searchButton.remove();
        this.searchButton = null;
      }

      this.showSearchButton = false;
    },
    _detachMapMediaQuery() {
      this._mapMediaQuery?.removeEventListener('change', this._mapMediaQueryHandler);
    },
    handleBeforeUnmount() {
      this._detachMapMediaQuery();
      if (this.autocomplete) {
        google.maps.event.clearInstanceListeners(this.autocomplete);
        this.autocomplete = null;
      }
    },
  },
};
</script>
